import { useContext } from 'react';

import { EditorContextPanelContext } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContext.ts';
import { useCopyToClipboard } from '@confluence/copy-content-link/entry-points/useCopyToClipboard.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';

import type { AnalyticsOrigin } from '../../constants/snippetAnalytics.ts';
import { ANALYTICS_UI_SUBJECT_ID } from '../../constants/snippetAnalytics.ts';
import { DeleteSnippetModal } from '../../modals/DeleteSnippetModal.tsx';
import { DuplicateSnippetModal } from '../../modals/DuplicateSnippetModal.tsx';
import { EditSnippetModal } from '../../modals/EditSnippetModal.tsx';
import type { Snippet } from '../../types/snippet.ts';
import { reportError } from '../../utils/reportError.ts';
import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';

export type SnippetActionHandlersOptions = {
	analyticsOrigin: AnalyticsOrigin;
	contentId?: string;
	spaceId?: string;
	spaceKey?: string;
};

export const useSnippetActionHandlers = ({
	analyticsOrigin,
	contentId = '',
	spaceId = '',
	spaceKey = '',
}: SnippetActionHandlersOptions) => {
	const { fireButtonClickedAnalytic } = useSnippetAnalytics(analyticsOrigin);
	const { showModal } = useDialogs();
	const { copyToClipboard } = useCopyToClipboard();
	const { editorAPI } = useContext(EditorContextPanelContext);

	const handleEdit = (snippet: Snippet): void => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.EDIT_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(EditSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
		});
	};

	const handleDuplicate = (snippet: Snippet): void => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.DUPLICATE_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(DuplicateSnippetModal, {
			snippet,
			editorAPI,
			contentId,
			spaceId,
			spaceKey,
			analyticsOrigin,
		});
	};

	const handleDelete = (snippet: Snippet): void => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.DELETE_SNIPPET, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
		});
		showModal(DeleteSnippetModal, { snippet, editorAPI });
	};

	const handleShare = async (snippet: Snippet): Promise<boolean> => {
		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.COPY_SNIPPET_LINK, {
			snippetId: snippet.snippetId,
			scope: snippet.scope,
			isShared: snippet.isShared,
		});

		if (!snippet.shareUrl) {
			reportError(
				new Error(`handleShare called without shareUrl for snippet ${snippet.snippetId}`),
			);
			return false;
		}

		const fullUrl =
			typeof window !== 'undefined'
				? `${window.location.origin}${snippet.shareUrl}`
				: snippet.shareUrl;
		try {
			await copyToClipboard(fullUrl);
			return true;
		} catch {
			return false;
		}
	};

	return { handleEdit, handleDuplicate, handleDelete, handleShare };
};
